import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AvatarWrapper } from '@/components/ui/avatar';
import { ProfileResume, ProfileResumeBody, ProfileResumeName } from '@/components/custom/profile';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useSession } from '@/hooks/useSession';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'neutral-lightest' },
  published: { label: 'Publicado', variant: 'success-light' }
};

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'published', label: 'Publicado' }
];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge
      variant={config.variant}
      size="xs"
    >
      {config.label}
    </Badge>
  );
}

function ChallengeRow({ challenge, onRefresh, currentUserId, isAdmin }) {
  const navigate = useNavigate();
  const { id, title, taxonomy, status, created_at, total_questions, user, is_public } = challenge;

  const formattedDate = created_at ? new Date(created_at).toLocaleDateString('pt-BR') : '—';
  const profileName = user?.profile_name;
  const isAuthor = currentUserId != null && user?.id != null && String(user.id) === String(currentUserId);
  const canManage = isAuthor || isAdmin;

  const handleActivate = async () => {
    try {
      const response = await liveChallengesApi.activate(id);
      const activationId = response?.data?.id;
      navigate(`/admin/desafio-ao-vivo/painel/${activationId}`);
    } catch (err) {
      const existingId = err?.response?.data?.activation_id;
      if (err?.response?.status === 409 && existingId) {
        toast('Você já tem uma ativação em andamento deste desafio — retomando.', { icon: 'ℹ️' });
        navigate(`/admin/desafio-ao-vivo/painel/${existingId}`);
        return;
      }
      const message = err?.response?.data?.error || 'Erro ao ativar desafio.';
      toast.error(message);
    }
  };

  // Gatilho draft→published (contrato do plano backend: PATCH update com status permitido).
  const handlePublish = async () => {
    try {
      await liveChallengesApi.update(id, { status: 'published' });
      toast.success('Desafio publicado — pronto para ativar.');
      onRefresh?.();
    } catch (err) {
      // update do backend responde {errors: [strings]} (full_messages), não JSON:API
      toast.error(err?.response?.data?.errors?.[0] || 'Erro ao publicar desafio.');
    }
  };

  const handleDuplicate = async () => {
    try {
      await liveChallengesApi.duplicate(id);
      toast.success('Desafio duplicado em rascunho.');
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao duplicar desafio.';
      toast.error(message);
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await liveChallengesApi.destroy(id);
      toast.success('Desafio excluído.');
      setConfirmingDelete(false);
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao excluir desafio.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="hover:bg-primary-lightest/30 flex flex-col gap-3 border-b border-neutral-lightest bg-white px-5 py-3 transition-colors md:flex-row md:items-center md:gap-6">
      <div className="w-full md:w-56 md:shrink-0">
        {user ? (
          <ProfileResume>
            <AvatarWrapper
              size="sm"
              src={user.image}
              text={user.name}
              className="shrink-0"
            />
            <ProfileResumeBody>
              <ProfileResumeName title={user.name}>{user.name}</ProfileResumeName>
              {profileName && (
                <Badge
                  variant="neutral-lightest"
                  size="xs"
                  className="font-light"
                  title={profileName}
                >
                  {profileName}
                </Badge>
              )}
            </ProfileResumeBody>
          </ProfileResume>
        ) : (
          <span className="text-xs text-neutral-light">—</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <h3 className="truncate text-sm font-semibold text-neutral-dark">{title}</h3>
          <StatusBadge status={status} />
          <Badge
            variant="neutral-lightest"
            size="xs"
            className="font-light"
          >
            {is_public ? 'Público' : 'Privado'}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-neutral">
          {taxonomy?.name || '—'} &middot; {total_questions ?? 0} questões &middot; {formattedDate} &middot; Última ativação: {challenge.last_activation_at ? new Date(challenge.last_activation_at).toLocaleString('pt-BR') : '—'}
        </p>
      </div>

      <div className="flex flex-wrap justify-end gap-2 md:w-[450px]">
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={handleDuplicate}
        >
          Duplicar para editar
        </Button>

        {canManage && status === 'draft' && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/editar/nome/${id}`)}
          >
            Editar
          </Button>
        )}

        {canManage && status === 'draft' && (
          <Button
            type="button"
            variant="default"
            size="xs"
            onClick={handlePublish}
          >
            Publicar
          </Button>
        )}

        {status === 'published' && (canManage || is_public) && (
          <Button
            type="button"
            variant="default"
            size="xs"
            onClick={handleActivate}
          >
            Ativar
          </Button>
        )}

        {canManage && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/${id}/ativacoes`)}
          >
            Ativações{typeof challenge.activations_count === 'number' ? ` (${challenge.activations_count})` : ''}
          </Button>
        )}

        {isAuthor && status === 'draft' && (
          <Button
            type="button"
            variant="outline-danger"
            size="xs"
            onClick={() => setConfirmingDelete(true)}
            title="Excluir desafio"
          >
            <FiTrash2 className="size-3.5" />
          </Button>
        )}
      </div>

      {confirmingDelete && (
        <ConfirmDeleteDialog
          challengeTitle={title}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
          loading={deleting}
        />
      )}
    </li>
  );
}

function ConfirmDeleteDialog({ challengeTitle, onConfirm, onCancel, loading }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dropshadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-foreground">Excluir desafio?</h3>
        <p className="mt-2 text-sm text-neutral">
          O desafio <strong>{challengeTitle}</strong> será removido permanentemente.
        </p>
        <p className="mt-2 text-xs text-neutral-light">
          Desafios duplicados a partir deste <strong>não</strong> serão afetados — eles têm cópias próprias.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LiveChallengeList() {
  const { session } = useSession();
  const currentUserId = session?.user?.id;
  const profileNames = session?.user?.profileNames || session?.user?.['profile-names'] || [];
  const isAdmin = profileNames.includes('Administrador');

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/admin/desafio-ao-vivo',
        name: 'Desafio ao Vivo',
        isActive: true
      }
    ]
  };

  const normalizeJsonApi = items =>
    items.map(item => ({
      id: item.id,
      ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
    }));

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const response = await liveChallengesApi.index();
      const items = response?.data ?? [];
      setChallenges(normalizeJsonApi(items));
    } catch {
      toast.error('Erro ao carregar desafios ao vivo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const filteredChallenges = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return challenges.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (normalizedSearch && !c.title?.toLowerCase().includes(normalizedSearch)) return false;
      return true;
    });
  }, [challenges, statusFilter, searchTerm]);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative md:w-72">
          <FiSearch
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-light"
          />
          <Input
            startIcon
            type="search"
            placeholder="Buscar por título..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          asChild
          variant="default"
          size="sm"
        >
          <Link to="/admin/desafio-ao-vivo/criar/nome">Criar Desafio</Link>
        </Button>
      </div>

      {loading ? (
        <Loader />
      ) : filteredChallenges.length > 0 ? (
        <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
          {filteredChallenges.map(challenge => (
            <ChallengeRow
              key={challenge.id}
              challenge={challenge}
              onRefresh={fetchChallenges}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
            />
          ))}
        </ul>
      ) : challenges.length === 0 ? (
        <EmptyState type="data" />
      ) : (
        // EmptyState não tem o type 'search' — caía no default 'Insira um texto.'
        // sem imagem. Reaproveitamos a ilustração de 'data' com texto explícito.
        <EmptyState
          type="data"
          text="Nenhum desafio encontrado para este filtro."
        />
      )}
    </DashboardContent>
  );
}
